import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useEmployeeAttendanceAuth } from '../context/EmployeeAttendanceAuthContext';
import { getPostLoginPath, STAFF_ATTENDANCE_UI_ENABLED } from '../utils/roleAccess';
import ErrorBoundary from '../../attendance/components/ErrorBoundary';
import EmployeeAttendanceLogin from '../components/Login';
import EmployeeAttendanceLayout from '../components/EmployeeAttendanceLayout';
import EmployeeDashboard from '../components/Dashboard';
import EmployeeHistory from '../components/History';
import EmployeeProfile from '../components/Profile';
import ChangePassword from '../components/ChangePassword';
import HrReportsDashboard from '../components/hr/HrReportsDashboard';
import HrEmployeesPage from '../components/hr/HrEmployeesPage';
import HrEmployeeFormPage from '../components/hr/HrEmployeeFormPage';
import HrAttendancePage from '../components/hr/HrAttendancePage';
import HrPortalAccessPage from '../components/hr/HrPortalAccessPage';
import HrHikvisionPunchesPage from '../components/hr/HrHikvisionPunchesPage';
import PostgraduateStudentsPage from '../components/postgraduate/PostgraduateStudentsPage';
import PostgraduateStudentFormPage from '../components/postgraduate/PostgraduateStudentFormPage';
import PgStudentListReportPage from '../components/postgraduate/PgStudentListReportPage';
import PgStudentViewPage from '../components/postgraduate/PgStudentViewPage';
import PgTeacherSheetPage from '../components/postgraduate/PgTeacherSheetPage';
import AdmissionsStudentsPage from '../components/admissions/AdmissionsStudentsPage';
import AdmissionsStudentFormPage from '../components/admissions/AdmissionsStudentFormPage';
import ProspectiveStudentsPage from '../components/admissions/ProspectiveStudentsPage';
import ProspectiveStudentFormPage from '../components/admissions/ProspectiveStudentFormPage';
import UgStudentListReportPage from '../components/admissions/UgStudentListReportPage';
import UgStudentViewPage from '../components/admissions/UgStudentViewPage';
import UgStudentFormPage from '../components/admissions/UgStudentFormPage';
import ExamEntriesPage from '../components/exams/ExamEntriesPage';
import ExamEntryFormPage from '../components/exams/ExamEntryFormPage';
import TeacherSheetMarksPage from '../components/exams/TeacherSheetMarksPage';
import SearchStudentMarksPage from '../components/exams/SearchStudentMarksPage';
import UtilitiesRoutes from '../../utilities/routes/UtilitiesRoutes';

function PortalLoading() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useEmployeeAttendanceAuth();

  if (loading) {
    return <PortalLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/bu-dashboard/login" replace />;
  }

  return children;
}

function DefaultHomeRedirect() {
  const { user } = useEmployeeAttendanceAuth();
  return <Navigate to={getPostLoginPath(user)} replace />;
}

function HrOnlyRoute({ children }) {
  const { isHr, isAuthenticated, loading, user } = useEmployeeAttendanceAuth();

  if (loading) {
    return <PortalLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/bu-dashboard/login" replace />;
  }

  if (!isHr) {
    return <Navigate to={getPostLoginPath(user)} replace />;
  }

  return children;
}

function AdmissionOnlyRoute({ children }) {
  const { isAdmission, isAuthenticated, loading, user } = useEmployeeAttendanceAuth();

  if (loading) {
    return <PortalLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/bu-dashboard/login" replace />;
  }

  if (!isAdmission) {
    return <Navigate to={getPostLoginPath(user)} replace />;
  }

  return children;
}

function ExamOnlyRoute({ children }) {
  const { isExam, isAuthenticated, loading, user } = useEmployeeAttendanceAuth();

  if (loading) {
    return <PortalLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/bu-dashboard/login" replace />;
  }

  if (!isExam) {
    return <Navigate to={getPostLoginPath(user)} replace />;
  }

  return children;
}

function PostgraduateOnlyRoute({ children }) {
  const { isPostgraduate, isAuthenticated, loading, user } = useEmployeeAttendanceAuth();
  if (loading) return <PortalLoading />;
  if (!isAuthenticated) return <Navigate to="/bu-dashboard/login" replace />;
  if (!isPostgraduate) return <Navigate to={getPostLoginPath(user)} replace />;
  return children;
}

function EmployeeAttendanceRoutes() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<EmployeeAttendanceLogin />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <EmployeeAttendanceLayout>
                <Routes>
                  <Route
                    path="/dashboard"
                    element={STAFF_ATTENDANCE_UI_ENABLED ? <EmployeeDashboard /> : <DefaultHomeRedirect />}
                  />
                  <Route
                    path="/history"
                    element={STAFF_ATTENDANCE_UI_ENABLED ? <EmployeeHistory /> : <DefaultHomeRedirect />}
                  />
                  <Route path="/utilities/*" element={<UtilitiesRoutes />} />
                  <Route path="/profile" element={<EmployeeProfile />} />
                  <Route path="/change-password" element={<ChangePassword />} />
                  <Route
                    path="/hr/reports"
                    element={
                      <HrOnlyRoute>
                        <HrReportsDashboard />
                      </HrOnlyRoute>
                    }
                  />
                  <Route
                    path="/hr/employees/new"
                    element={
                      <HrOnlyRoute>
                        <HrEmployeeFormPage />
                      </HrOnlyRoute>
                    }
                  />
                  <Route
                    path="/hr/employees/:employeeId"
                    element={
                      <HrOnlyRoute>
                        <HrEmployeeFormPage />
                      </HrOnlyRoute>
                    }
                  />
                  <Route
                    path="/hr/employees"
                    element={
                      <HrOnlyRoute>
                        <HrEmployeesPage />
                      </HrOnlyRoute>
                    }
                  />
                  <Route
                    path="/hr/attendance"
                    element={
                      <HrOnlyRoute>
                        <HrAttendancePage />
                      </HrOnlyRoute>
                    }
                  />
                  <Route
                    path="/hr/portal-access"
                    element={
                      <HrOnlyRoute>
                        <HrPortalAccessPage />
                      </HrOnlyRoute>
                    }
                  />
                  <Route
                    path="/hr/hikvision/punches"
                    element={
                      <HrOnlyRoute>
                        <HrHikvisionPunchesPage />
                      </HrOnlyRoute>
                    }
                  />
                  <Route
                    path="/hr"
                    element={<Navigate to="/bu-dashboard/hr/attendance" replace />}
                  />
                  <Route
                    path="/postgraduate/student-list/view"
                    element={<PostgraduateOnlyRoute><PgStudentViewPage /></PostgraduateOnlyRoute>}
                  />
                  <Route
                    path="/postgraduate/student-list"
                    element={<PostgraduateOnlyRoute><PgStudentListReportPage /></PostgraduateOnlyRoute>}
                  />
                <Route
                    path="/postgraduate/teacher-sheet"
                    element={<PostgraduateOnlyRoute><PgTeacherSheetPage /></PostgraduateOnlyRoute>}
                  />
                  <Route
                    path="/postgraduate/students/new"
                    element={<PostgraduateOnlyRoute><PostgraduateStudentFormPage key="pg-new" /></PostgraduateOnlyRoute>}
                  />
                  <Route
                    path="/postgraduate/students/:studentId"
                    element={<PostgraduateOnlyRoute><PostgraduateStudentFormPage key="pg-edit" /></PostgraduateOnlyRoute>}
                  />
                  <Route
                    path="/postgraduate/students"
                    element={<PostgraduateOnlyRoute><PostgraduateStudentsPage /></PostgraduateOnlyRoute>}
                  />
                  <Route
                    path="/admissions/students/new"
                    element={
                      <AdmissionOnlyRoute>
                        <AdmissionsStudentFormPage key="new" />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/students/:studentId"
                    element={
                      <AdmissionOnlyRoute>
                        <AdmissionsStudentFormPage key="edit" />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/students"
                    element={
                      <AdmissionOnlyRoute>
                        <AdmissionsStudentsPage />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/prospective/new"
                    element={
                      <AdmissionOnlyRoute>
                        <ProspectiveStudentFormPage key="prospective-new" />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/prospective/:prospectiveId"
                    element={
                      <AdmissionOnlyRoute>
                        <ProspectiveStudentFormPage key="prospective-edit" />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/prospective"
                    element={
                      <AdmissionOnlyRoute>
                        <ProspectiveStudentsPage />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/ug-student-list/view"
                    element={
                      <AdmissionOnlyRoute>
                        <UgStudentViewPage />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/ug-student-list/:studentId"
                    element={
                      <AdmissionOnlyRoute>
                        <UgStudentFormPage />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions/ug-student-list"
                    element={
                      <AdmissionOnlyRoute>
                        <UgStudentListReportPage />
                      </AdmissionOnlyRoute>
                    }
                  />
                  <Route
                    path="/admissions"
                    element={
                      <Navigate to="/bu-dashboard/admissions/students" replace />
                    }
                  />
                  <Route
                    path="/exams/entries/new"
                    element={
                      <ExamOnlyRoute>
                        <ExamEntryFormPage key="exam-new" />
                      </ExamOnlyRoute>
                    }
                  />
                  <Route
                    path="/exams/entries/:entryId"
                    element={
                      <ExamOnlyRoute>
                        <ExamEntryFormPage key="exam-edit" />
                      </ExamOnlyRoute>
                    }
                  />
                  <Route
                    path="/exams/entries"
                    element={
                      <ExamOnlyRoute>
                        <ExamEntriesPage />
                      </ExamOnlyRoute>
                    }
                  />
                  <Route
                    path="/exams/teacher-sheets/:sheetId"
                    element={
                      <ExamOnlyRoute>
                        <Navigate to="/bu-dashboard/exams/teacher-sheets" replace />
                      </ExamOnlyRoute>
                    }
                  />
                  <Route
                    path="/exams/teacher-sheets"
                    element={
                      <ExamOnlyRoute>
                        <TeacherSheetMarksPage />
                      </ExamOnlyRoute>
                    }
                  />
                  <Route
                    path="/exams/search-student-marks"
                    element={
                      <ExamOnlyRoute>
                        <SearchStudentMarksPage />
                      </ExamOnlyRoute>
                    }
                  />
                  <Route
                    path="/exams"
                    element={<Navigate to="/bu-dashboard/exams/entries" replace />}
                  />
                  <Route
                    path="/reports"
                    element={
                      STAFF_ATTENDANCE_UI_ENABLED
                        ? <Navigate to="/bu-dashboard/history" replace />
                        : <DefaultHomeRedirect />
                    }
                  />
                  <Route path="/" element={<DefaultHomeRedirect />} />
                  <Route path="*" element={<DefaultHomeRedirect />} />
                </Routes>
              </EmployeeAttendanceLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
}

export default EmployeeAttendanceRoutes;
